import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { computeDayWorkSeconds } from '../lib/hourBalance';
import {
  DEFAULT_TIME_ZONE, timeZoneForWorker, timeZoneLabel, dayKeyInZone, zonedParts,
  addDaysToDayKey, dowOfDayKey,
} from '../lib/workerTime';

const DOW_NAME = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const pad = (n, z = 2) => String(n).padStart(z, '0');

// Solo para las fechas del rango ELEGIDAS en el selector (Date local =
// etiqueta de calendario, no un instante). Los eventos NUNCA pasan por aquí:
// su día se decide en la zona del trabajador (dayKeyInZone).
const isoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// 'DD/MM' o 'DD/MM/YYYY' de una clave de día (aritmética pura).
const fmtDayKey = (key, withYear = false) => `${key.slice(8, 10)}/${key.slice(5, 7)}${withYear ? `/${key.slice(0, 4)}` : ''}`;

// Hora de reloj 'HH:mm:ss' del fichaje en la zona del trabajador.
const fmtTime = (iso, tz = DEFAULT_TIME_ZONE) => {
  if (!iso) return '';
  const p = zonedParts(iso, tz);
  return p ? `${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}` : '';
};

const fmtHMS = (totalSeconds) => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
};

// Resumen diario: { workedSeconds, pauseSeconds, firstIn, lastOut }.
// Sale del cómputo ÚNICO compartido (computeDayWork, src/lib/hourBalance.js)
// en segundos: NINGUNA pausa cuenta como trabajada, todas se restan
// (decisión de producto 2026-09-21; antes las pausas dentro de la jornada
// contaban como trabajadas). Los fichajes/pausas rechazados no cuentan.
const computeDay = (dayEvents, tz = DEFAULT_TIME_ZONE) => {
  const { workedSeconds, pauseSeconds, firstIn, lastOut } = computeDayWorkSeconds(dayEvents, { timeZone: tz });
  return { workedSeconds, pauseSeconds, firstIn, lastOut };
};

const PAUSE_COLUMN = 'PAUSAS (DESCONTADAS)';

// Agrupa por día EN LA ZONA DEL TRABAJADOR (no la del navegador que exporta).
const groupEventsByDay = (events, tz = DEFAULT_TIME_ZONE) => {
  const map = new Map();
  for (const e of events) {
    if (!e.timestamp) continue;
    const fecha = dayKeyInZone(e.timestamp, tz);
    if (!fecha) continue;
    if (!map.has(fecha)) map.set(fecha, []);
    map.get(fecha).push(e);
  }
  return map;
};

const fmtDayShort = (d) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;

// `workhoursByDow` se sigue aceptando (callers existentes) pero ya NO se usa:
// todas las pausas se restan, estén dentro o fuera de la jornada.
// `timeZone` (opcional): zona del trabajador; si no viene, se deriva de
// `worker` (comunidad/provincia). Días y horas del informe van en esa zona.
const renderWorkerRangePage = (doc, { worker, company, workplaceName, startDate, endDate, events, timeZone }) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const tz = timeZone || timeZoneForWorker(worker);
  const tzLabel = timeZoneLabel(tz);

  doc.setFillColor(14, 165, 233);
  doc.circle(18, 16, 5, 'F');
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('TemControl', 26, 19);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(15, 23, 42);
  doc.text('Listado detallado del registro de jornada', pageWidth / 2, 18, { align: 'center' });

  autoTable(doc, {
    startY: 28,
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 2 },
    body: [
      [{ content: `Empresa: ${company?.name || '—'}`, styles: { fillColor: [248, 250, 252] } }, { content: `Empleado: ${worker?.name || '—'}`, styles: { fillColor: [248, 250, 252] } }],
      [{ content: `CIF: ${company?.cif || '—'}` }, { content: `NIF: ${worker?.nif || '—'}` }],
      [{ content: `Sede: ${workplaceName || (worker?.workingSite != null ? '#' + worker.workingSite : '—')}` }, { content: `Nº Seg. Social: ${worker?.ssNumber || '—'}` }],
      [{ content: `Período: ${fmtDayShort(startDate)} – ${fmtDayShort(endDate)}` }, { content: tzLabel ? `Horas en ${tzLabel.toLowerCase()}` : '' }],
    ],
  });

  const eventsByDay = groupEventsByDay(events, tz);
  const rows = [];
  let totalWorkedSeconds = 0;
  let totalPauseSeconds = 0;

  // Recorremos CLAVES de día (aritmética pura): el rango elegido son fechas
  // de calendario, no instantes.
  const endKey = isoDate(endDate);
  for (let iso = isoDate(startDate); iso && iso <= endKey; iso = addDaysToDayKey(iso, 1)) {
    const dayEvents = eventsByDay.get(iso) || [];
    const dayLabel = `${DOW_NAME[dowOfDayKey(iso)]} ${fmtDayKey(iso)}`;
    if (dayEvents.length === 0) {
      rows.push([dayLabel, '', '', '', '']);
      continue;
    }
    const { workedSeconds, pauseSeconds, firstIn, lastOut } = computeDay(dayEvents, tz);
    totalWorkedSeconds += workedSeconds;
    totalPauseSeconds += pauseSeconds;
    rows.push([dayLabel, fmtTime(firstIn, tz), fmtTime(lastOut, tz), fmtHMS(pauseSeconds), fmtHMS(workedSeconds)]);
  }

  rows.push([
    { content: 'TOTAL DE HORAS', colSpan: 3, styles: { fontStyle: 'bold', halign: 'right' } },
    { content: fmtHMS(totalPauseSeconds), styles: { fontStyle: 'bold' } },
    { content: fmtHMS(totalWorkedSeconds), styles: { fontStyle: 'bold' } },
  ]);

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 4,
    theme: 'grid',
    head: [['DÍA', 'ENTRADA', 'SALIDA', PAUSE_COLUMN, 'HORAS ORDINARIAS']],
    headStyles: { fillColor: [14, 165, 233], textColor: 255, fontStyle: 'bold', halign: 'center' },
    styles: { fontSize: 8.5, cellPadding: 1.8, halign: 'center' },
    columnStyles: { 0: { halign: 'left' } },
    body: rows,
    didDrawPage: () => {
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.setFont('helvetica', 'normal');
      const footer = 'Registro realizado en cumplimiento del Real Decreto-ley 8/2019, de 8 de marzo, de medidas urgentes de protección social y lucha contra la precariedad laboral en la jornada de trabajo ("RDL 8/2019").';
      const split = doc.splitTextToSize(footer, pageWidth - 30);
      doc.text(split, 15, pageHeight - 18);
      doc.setFontSize(9);
      doc.setTextColor(15, 23, 42);
      doc.text('Firma de la empresa', 20, pageHeight - 30);
      doc.text('Firma del empleado', pageWidth - 60, pageHeight - 30);
      doc.line(20, pageHeight - 27, 80, pageHeight - 27);
      doc.line(pageWidth - 70, pageHeight - 27, pageWidth - 20, pageHeight - 27);
    },
    margin: { bottom: 40 },
  });
};

// ============================================================
// PDF multi-trabajador (uno por página, rango libre desde/hasta)
// ============================================================
export const generateBulkRangePdf = ({ entries, startDate, endDate }) => {
  // entries: [{ worker, company, events }]
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  entries.forEach((entry, idx) => {
    if (idx > 0) doc.addPage();
    renderWorkerRangePage(doc, { ...entry, startDate, endDate });
  });
  const stamp = `${isoDate(startDate)}_${isoDate(endDate)}`;
  const filename = entries.length === 1
    ? `informe_${entries[0].worker?.name?.replace(/\s+/g, '_') || 'trabajador'}_${stamp}.pdf`
    : `informes_${stamp}_${entries.length}trabajadores.pdf`;
  doc.save(filename);
};

// ============================================================
// XLSX: hoja por empresa para un rango de fechas arbitrario
// ============================================================
// Estructura:
//   - Una columna por mes incluido en el rango (puede ser uno solo o varios).
//   - Cada celda muestra las horas trabajadas en ese mes DENTRO del rango.
//   - Última columna = total del rango.
// `workhoursByWorker` se sigue aceptando (callers existentes) pero ya NO se
// usa: todas las pausas se restan (computeDayWork), haya horario o no.
const buildRangeSheet = ({ workers, eventsByWorker, startDate, endDate }) => {
  // Calculamos los meses que cae el rango (inclusive ambos extremos).
  const monthsInRange = []; // [{ year, month0, label }]
  const cursor = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const last = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
  while (cursor <= last) {
    monthsInRange.push({
      year: cursor.getFullYear(),
      month0: cursor.getMonth(),
      label: `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`,
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  // Si por alguna razón el rango está vacío, fallback a un mes.
  if (monthsInRange.length === 0) {
    monthsInRange.push({
      year: startDate.getFullYear(),
      month0: startDate.getMonth(),
      label: `${MONTHS[startDate.getMonth()]} ${startDate.getFullYear()}`,
    });
  }
  // Límites del rango como claves de día (comparación de strings, pura).
  const startKey = isoDate(startDate);
  const endKey = isoDate(endDate);

  const rowsData = workers.map((w) => {
    const workerEvents = eventsByWorker[w.id] || [];
    // Cada trabajador en SU zona (Canarias / península).
    const tz = timeZoneForWorker(w);
    const byDay = groupEventsByDay(workerEvents, tz);
    const monthlyTotals = Array.from({ length: monthsInRange.length }, () => 0);
    for (const [fecha, dayEvents] of byDay.entries()) {
      // Solo días dentro del rango.
      if (fecha < startKey || fecha > endKey) continue;
      const fy = Number(fecha.slice(0, 4));
      const fm0 = Number(fecha.slice(5, 7)) - 1;
      const idx = monthsInRange.findIndex((m) => m.year === fy && m.month0 === fm0);
      if (idx < 0) continue;
      const { workedSeconds } = computeDay(dayEvents, tz);
      monthlyTotals[idx] += workedSeconds;
    }
    const totalSeconds = monthlyTotals.reduce((s, v) => s + v, 0);
    return {
      name: w.name || `#${w.id}`,
      monthly: monthlyTotals.map(fmtHMS),
      total: fmtHMS(totalSeconds),
      totalSeconds,
    };
  }).sort((a, b) => b.totalSeconds - a.totalSeconds);

  const fmtDay = (d) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  const titleRow = [`Informe ${fmtDay(startDate)} – ${fmtDay(endDate)}`, ...Array(monthsInRange.length + 1).fill('')];
  const header = ['Empleado', ...monthsInRange.map((m) => m.label), 'Total'];
  const dataRows = rowsData.map((r) => [r.name, ...r.monthly, r.total]);

  const aoa = [titleRow, header, ...dataRows];
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  ws['!cols'] = [
    { wch: 28 },
    ...monthsInRange.map(() => ({ wch: 16 })),
    { wch: 14 },
  ];

  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: monthsInRange.length + 1 } },
  ];

  const headerRowIdx = 1;
  for (let c = 0; c <= monthsInRange.length + 1; c++) {
    const cellRef = XLSX.utils.encode_cell({ r: headerRowIdx, c });
    if (!ws[cellRef]) continue;
    ws[cellRef].s = {
      font: { bold: true, color: { rgb: 'FFFFFFFF' } },
      fill: { fgColor: { rgb: 'FFF97316' } },
      alignment: { horizontal: 'center', vertical: 'center' },
    };
  }
  const titleRef = XLSX.utils.encode_cell({ r: 0, c: 0 });
  if (ws[titleRef]) {
    ws[titleRef].s = {
      font: { bold: true, sz: 13, color: { rgb: 'FF0F172A' } },
      alignment: { horizontal: 'left', vertical: 'center' },
    };
  }
  return ws;
};

const sanitizeSheetName = (name) => (name || 'Empresa').replace(/[\\/?*[\]]/g, '').slice(0, 28);

// Nombre de hoja para el Excel detallado por trabajador: queremos que sea
// reconocible (nombre + 4 últimas cifras del NIF) y que respete el límite
// de Excel (31 chars). Si no hay NIF usamos solo el nombre.
const sheetNameForWorker = (worker) => {
  const base = (worker?.name || `Trabajador ${worker?.id || ''}`).trim();
  const nif = (worker?.nif || '').trim();
  // Cogemos los últimos 4 chars del NIF (es suficiente para desambiguar
  // entre homónimos pero no expone el documento entero como tab visible).
  const nifTag = nif ? ` (${nif.slice(-4)})` : '';
  return sanitizeSheetName(`${base}${nifTag}`).slice(0, 31);
};

// ============================================================
// XLSX: Informe por rango — una hoja por empresa
// ============================================================
export const generateBulkRangeExcel = ({ companiesData, startDate, endDate }) => {
  const wb = XLSX.utils.book_new();
  const usedNames = new Set();
  companiesData.forEach(({ company, workers, eventsByWorker }) => {
    const ws = buildRangeSheet({ workers, eventsByWorker, startDate, endDate });
    let sheetName = sanitizeSheetName(company?.name || 'Empresa');
    let i = 2;
    while (usedNames.has(sheetName)) {
      sheetName = sanitizeSheetName(`${company?.name || 'Empresa'} (${i++})`);
    }
    usedNames.add(sheetName);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  });
  const tag = `${isoDate(startDate)}_${isoDate(endDate)}`;
  const filename = companiesData.length === 1
    ? `informe_${companiesData[0].company?.name?.replace(/\s+/g, '_') || 'empresa'}_${tag}.xlsx`
    : `informe_${tag}_${companiesData.length}empresas.xlsx`;
  XLSX.writeFile(wb, filename);
};

// ============================================================
// XLSX DETALLADO — una hoja por trabajador, formato tipo PDF
// ============================================================
// Misma información que el PDF (cabecera + tabla diaria DÍA / ENTRADA /
// SALIDA / PAUSAS (DESCONTADAS) / HORAS ORDINARIAS + fila TOTAL), pero en Excel para que
// el admin pueda manipular los datos. Cada hoja del libro corresponde a
// un trabajador y se nombra con su nombre + 4 últimas cifras del NIF.
// Acepta `entries: [{ worker, company, workplaceName, events, workhoursByDow }]`,
// exactamente la misma estructura que `generateBulkRangePdf` para que el
// caller pueda alternar de formato sin refetchar.
const buildDetailedWorkerSheet = ({ worker, company, workplaceName, events, startDate, endDate, timeZone }) => {
  const tz = timeZone || timeZoneForWorker(worker);
  const tzLabel = timeZoneLabel(tz);
  const eventsByDay = groupEventsByDay(events || [], tz);
  const fmtDay = (d) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;

  // Cabecera: 4 filas con info del trabajador y la empresa. Misma
  // distribución que el PDF para que el admin reconozca el formato.
  const headerRows = [
    [`Listado detallado del registro de jornada`, '', '', '', ''],
    [`Empresa: ${company?.name || '—'}`, '', '', `Empleado: ${worker?.name || '—'}`, ''],
    [`CIF: ${company?.cif || '—'}`, '', '', `NIF: ${worker?.nif || '—'}`, ''],
    [
      `Sede: ${workplaceName || (worker?.workingSite != null ? '#' + worker.workingSite : '—')}`,
      '', '',
      `Nº Seg. Social: ${worker?.ssNumber || '—'}`,
      '',
    ],
    [`Período: ${fmtDay(startDate)} – ${fmtDay(endDate)}`, '', '', tzLabel ? `Horas en ${tzLabel.toLowerCase()}` : '', ''],
    [], // fila vacía de separación
  ];

  // Tabla diaria + totales
  const tableHeader = ['DÍA', 'ENTRADA', 'SALIDA', PAUSE_COLUMN, 'HORAS ORDINARIAS'];
  const dataRows = [];
  let totalWorked = 0;
  let totalPause = 0;
  const endKey = isoDate(endDate);
  for (let iso = isoDate(startDate); iso && iso <= endKey; iso = addDaysToDayKey(iso, 1)) {
    const dayEvents = eventsByDay.get(iso) || [];
    const dayLabel = `${DOW_NAME[dowOfDayKey(iso)]} ${fmtDayKey(iso, true)}`;
    if (dayEvents.length === 0) {
      dataRows.push([dayLabel, '', '', '', '']);
      continue;
    }
    const { workedSeconds, pauseSeconds, firstIn, lastOut } = computeDay(dayEvents, tz);
    totalWorked += workedSeconds;
    totalPause += pauseSeconds;
    dataRows.push([
      dayLabel,
      fmtTime(firstIn, tz),
      fmtTime(lastOut, tz),
      fmtHMS(pauseSeconds),
      fmtHMS(workedSeconds),
    ]);
  }
  const totalRow = ['', '', 'TOTAL DE HORAS', fmtHMS(totalPause), fmtHMS(totalWorked)];

  const aoa = [...headerRows, tableHeader, ...dataRows, totalRow];
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Anchos de columna pensados para el formato del PDF.
  ws['!cols'] = [
    { wch: 28 }, // DÍA
    { wch: 14 }, // ENTRADA
    { wch: 14 }, // SALIDA
    { wch: 22 }, // PAUSAS (DESCONTADAS)
    { wch: 18 }, // HORAS ORDINARIAS
  ];

  // Merges de la cabecera para que las etiquetas largas no se corten:
  //   - Título: A1:E1
  //   - Empresa/CIF/Sede/Período: A:C
  //   - Empleado/NIF/SS: D:E
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 2 } },
    { s: { r: 1, c: 3 }, e: { r: 1, c: 4 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: 2 } },
    { s: { r: 2, c: 3 }, e: { r: 2, c: 4 } },
    { s: { r: 3, c: 0 }, e: { r: 3, c: 2 } },
    { s: { r: 3, c: 3 }, e: { r: 3, c: 4 } },
    { s: { r: 4, c: 0 }, e: { r: 4, c: 4 } },
  ];

  // Estilos: aplicarlos solo en visores compatibles (xlsx-js-style). En el
  // xlsx estándar son ignorados pero no rompen el archivo.
  const titleRef = XLSX.utils.encode_cell({ r: 0, c: 0 });
  if (ws[titleRef]) {
    ws[titleRef].s = {
      font: { bold: true, sz: 13, color: { rgb: 'FF0F172A' } },
      alignment: { horizontal: 'center', vertical: 'center' },
    };
  }
  const headerRowIdx = headerRows.length; // donde empieza tableHeader
  for (let c = 0; c < tableHeader.length; c++) {
    const ref = XLSX.utils.encode_cell({ r: headerRowIdx, c });
    if (!ws[ref]) continue;
    ws[ref].s = {
      font: { bold: true, color: { rgb: 'FFFFFFFF' } },
      fill: { fgColor: { rgb: 'FF0EA5E9' } },
      alignment: { horizontal: 'center', vertical: 'center' },
    };
  }
  // Fila de total: negrita.
  const totalRowIdx = headerRowIdx + 1 + dataRows.length;
  for (let c = 0; c < tableHeader.length; c++) {
    const ref = XLSX.utils.encode_cell({ r: totalRowIdx, c });
    if (!ws[ref]) continue;
    ws[ref].s = {
      font: { bold: true },
      fill: { fgColor: { rgb: 'FFF1F5F9' } },
    };
  }

  return ws;
};

export const generateBulkRangeExcelDetailed = ({ entries, startDate, endDate }) => {
  const wb = XLSX.utils.book_new();
  const usedNames = new Set();
  for (const entry of entries) {
    const ws = buildDetailedWorkerSheet({ ...entry, startDate, endDate });
    let name = sheetNameForWorker(entry.worker);
    // Garantizar unicidad: si dos trabajadores comparten nombre y NIF
    // corto, anteponemos un sufijo numérico.
    let i = 2;
    let candidate = name;
    while (usedNames.has(candidate)) {
      const suffix = ` (${i++})`;
      candidate = (name.slice(0, 31 - suffix.length) + suffix).slice(0, 31);
    }
    usedNames.add(candidate);
    XLSX.utils.book_append_sheet(wb, ws, candidate);
  }
  const tag = `${isoDate(startDate)}_${isoDate(endDate)}`;
  const filename = entries.length === 1
    ? `informe_detallado_${entries[0].worker?.name?.replace(/\s+/g, '_') || 'trabajador'}_${tag}.xlsx`
    : `informe_detallado_${tag}_${entries.length}trabajadores.xlsx`;
  XLSX.writeFile(wb, filename);
};
